import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Scissors, Mail, Lock, User, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [saloneName, setSaloneName] = useState("I CIRILLO PARRUCCHIERI");
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ nome: "", email: "", password: "" });
  // "Resta connesso" — quando true (default) salva il token in localStorage (persistente tra sessioni/deploy app PWA)
  const [rememberMe, setRememberMe] = useState(true);

  useEffect(() => {
    // Fetch logo from config (no auth needed for login page)
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${API}/configurazione/public`);
        if (res.data?.logo_url) {
          setLogoUrl(res.data.logo_url);
        }
        if (res.data?.nome_salone) {
          setSaloneName(res.data.nome_salone);
        }
      } catch (error) {
        // Logo opzionale: usa default se non disponibile
        console.warn('Logo salone non disponibile:', error?.message || error);
      }
    };
    fetchLogo();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password, rememberMe);
      toast.success("Benvenuto!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.nome);
      toast.success("Account creato con successo!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container min-h-screen">
      {/* Left side - Image */}
      <div 
        className="login-image hidden md:block relative"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/7195812/pexels-photo-7195812.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260')"
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-indigo-900/70 to-transparent flex flex-col justify-end p-12">
          <h2 className="text-5xl font-bold text-white mb-4 tracking-tight">
            Gestisci il tuo salone con stile
          </h2>
          <p className="text-blue-100 text-lg max-w-md leading-relaxed">
            Appuntamenti, clienti, magazzino e cassa in un'unica piattaforma elegante e intuitiva.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-10">
            {logoUrl ? (
              <img 
                src={logoUrl.startsWith('/') ? `${API.replace('/api', '')}${logoUrl}` : logoUrl} 
                alt="Logo" 
                className="w-14 h-14 rounded-2xl object-cover shadow-xl"
              />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-xl shadow-blue-600/30">
                <Scissors className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
                {saloneName}
              </h1>
              <p className="text-sm text-blue-600 font-medium">Gestionale Parrucchiere</p>
            </div>
          </div>

          <Card className="border-0 shadow-2xl shadow-slate-200/60 rounded-3xl overflow-hidden">
            <Tabs defaultValue="login" className="w-full">
              <CardHeader className="pb-4 pt-6 px-6">
                <TabsList className="grid w-full grid-cols-2 bg-slate-100/80 rounded-xl p-1">
                  <TabsTrigger value="login" data-testid="login-tab" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">Accedi</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">Registrati</TabsTrigger>
                </TabsList>
              </CardHeader>

              <CardContent className="px-6 pb-6">
                <TabsContent value="login" className="mt-0">
                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="email@esempio.it"
                          className="pl-10"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          data-testid="login-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-password"
                          type={showLoginPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                          title={showLoginPassword ? "Nascondi password" : "Mostra password"}
                          data-testid="login-password-toggle"
                          tabIndex={-1}
                        >
                          {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Resta connesso — salva token in localStorage per persistere tra sessioni e deploy */}
                    <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        data-testid="login-remember-me"
                      />
                      Resta connesso
                    </label>

                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl shadow-lg shadow-blue-500/25 h-12 text-base font-semibold"
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? "Accesso in corso..." : "Accedi"}
                    </Button>
                    
                    <a 
                      href="/reset-password"
                      className="block text-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Password dimenticata?
                    </a>
                  </form>
                </TabsContent>

                <TabsContent value="register" className="mt-0">
                  <form onSubmit={handleRegister} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="register-nome">Nome</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-nome"
                          type="text"
                          placeholder="Il tuo nome"
                          className="pl-10"
                          value={registerData.nome}
                          onChange={(e) => setRegisterData({ ...registerData, nome: e.target.value })}
                          required
                          data-testid="register-nome"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="email@esempio.it"
                          className="pl-10"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          required
                          data-testid="register-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="register-password"
                          type={showRegisterPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          required
                          data-testid="register-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegisterPassword(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                          title={showRegisterPassword ? "Nascondi password" : "Mostra password"}
                          data-testid="register-password-toggle"
                          tabIndex={-1}
                        >
                          {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl shadow-lg shadow-blue-500/25 h-12 text-base font-semibold"
                      disabled={loading}
                      data-testid="register-submit"
                    >
                      {loading ? "Registrazione..." : "Crea Account"}
                    </Button>
                  </form>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>

          <p className="text-center text-sm text-slate-400 mt-8">
            Gestionale professionale per saloni di parrucchiere
          </p>
        </div>
      </div>
    </div>
  );
}
